import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";
import { commissionsApi } from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, Card, Input } from "@/design-system/components/ui";
import { currency } from "@/components/commissions/formatters";
import { commissionAmount } from "@/components/commissions/commissionMath";

interface Row {
  loanReference: string;
  revenuePoolId: string;
  serviceDescription: string;
  grossRevenue: string;
  commissionPercent: string;
  error?: string;
}

function emptyRow(): Row {
  return {
    loanReference: "",
    revenuePoolId: "",
    serviceDescription: "",
    grossRevenue: "",
    commissionPercent: "",
  };
}

export function BulkEntryPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const pools = useQuery({
    queryKey: commissionKeys.poolList("active"),
    queryFn: () => commissionsApi.listPools(false),
  });

  const [rows, setRows] = useState<Row[]>(() => [emptyRow(), emptyRow(), emptyRow()]);
  const [topError, setTopError] = useState<string | null>(null);

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (i: number) => setRows((prev) => prev.filter((_, idx) => idx !== i));

  const validateRow = (r: Row): string | undefined => {
    if (!r.revenuePoolId) return "Pick a pool";
    if (r.serviceDescription.trim().length < 10) return "Description is too short";
    const gross = Number(r.grossRevenue);
    if (!gross || gross <= 0) return "Gross revenue must be > 0";
    const pct = Number(r.commissionPercent);
    if (!pct || pct <= 0 || pct > 100) return "Percent must be 0.01–100";
    return undefined;
  };

  const submitAll = useMutation({
    mutationFn: async () => {
      const withErrors = rows.map((r) => ({ ...r, error: validateRow(r) }));
      setRows(withErrors);
      const valid = withErrors.filter((r) => !r.error);
      if (valid.length === 0) {
        throw new Error("No valid rows to submit.");
      }
      const createdIds: string[] = [];
      for (const r of valid) {
        const claim = await commissionsApi.createClaim({
          loanId: null,
          loanReference: r.loanReference.trim() || null,
          revenuePoolId: r.revenuePoolId,
          serviceDescription: r.serviceDescription.trim(),
          grossRevenue: Number(r.grossRevenue),
          commissionPercent: Number(r.commissionPercent),
        });
        createdIds.push(claim.summary.id);
      }
      await commissionsApi.bulkSubmit(createdIds);
      return createdIds.length;
    },
    onSuccess: () => {
      // The destination dashboard's queue/stats/my-claims reads are cached
      // (staleTime 5min) — mark the whole module stale before landing there.
      void qc.invalidateQueries({ queryKey: commissionKeys.all });
      navigate("/commissions");
    },
    onError: (e) => setTopError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const totalValid = rows.filter((r) => !validateRow(r)).length;
  const totalCommission = rows.reduce((acc, r) => {
    const gross = Number(r.grossRevenue) || 0;
    const pct = Number(r.commissionPercent) || 0;
    return acc + (gross * pct) / 100;
  }, 0);

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex items-end justify-between gap-space-3">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            Commissions
          </div>
          <h1 className="font-display font-extrabold text-h2 uppercase tracking-wider text-onyx m-0">
            Bulk entry
          </h1>
          <p className="text-body text-gray-mid mt-space-2 max-w-reading">
            Spreadsheet-style grid for logging multiple claims at once. Each row is validated on
            submit and invalid rows are skipped — valid rows become drafts, then submit to
            Accounting in one batch.
          </p>
        </div>
        <Button variant="secondary" onClick={addRow}>
          <Plus size={14} strokeWidth={2} aria-hidden /> Add row
        </Button>
      </div>

      {topError && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{topError}</span>
        </div>
      )}

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-small">
            <thead className="bg-onyx">
              <tr>
                <th className="px-space-2 py-space-2 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  #
                </th>
                <th className="px-space-2 py-space-2 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Loan ref
                </th>
                <th className="px-space-2 py-space-2 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Pool
                </th>
                <th className="px-space-2 py-space-2 text-left font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Description
                </th>
                <th className="px-space-2 py-space-2 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Gross
                </th>
                <th className="px-space-2 py-space-2 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  %
                </th>
                <th className="px-space-2 py-space-2 text-right font-display text-[10px] font-semibold uppercase tracking-label text-gold">
                  Commission
                </th>
                <th className="px-space-2 py-space-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const gross = Number(r.grossRevenue) || 0;
                const pct = Number(r.commissionPercent) || 0;
                const amount = commissionAmount(gross, pct);
                return (
                  <tr key={i} className="border-t border-border-light">
                    <td className="px-space-2 py-space-2 text-gray-mid tabular-nums">{i + 1}</td>
                    <td className="px-space-2 py-space-2">
                      <Input
                        value={r.loanReference}
                        onChange={(e) => setRow(i, { loanReference: e.target.value })}
                        placeholder="optional"
                      />
                    </td>
                    <td className="px-space-2 py-space-2">
                      <select
                        value={r.revenuePoolId}
                        onChange={(e) => setRow(i, { revenuePoolId: e.target.value })}
                        className="w-full h-10 px-space-2 border border-border-light rounded-md font-body text-small bg-white-pure"
                      >
                        <option value="">—</option>
                        {pools.data?.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.code} — {p.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-space-2 py-space-2 min-w-[280px]">
                      <Input
                        value={r.serviceDescription}
                        onChange={(e) => setRow(i, { serviceDescription: e.target.value })}
                        invalid={!!r.error && r.serviceDescription.trim().length < 10}
                        placeholder="min 10 chars"
                      />
                    </td>
                    <td className="px-space-2 py-space-2 w-[140px]">
                      <Input
                        type="number"
                        step="0.01"
                        value={r.grossRevenue}
                        onChange={(e) => setRow(i, { grossRevenue: e.target.value })}
                        className="text-right tabular-nums"
                      />
                    </td>
                    <td className="px-space-2 py-space-2 w-[100px]">
                      <Input
                        type="number"
                        step="0.01"
                        value={r.commissionPercent}
                        onChange={(e) => setRow(i, { commissionPercent: e.target.value })}
                        className="text-right tabular-nums"
                      />
                    </td>
                    <td className="px-space-2 py-space-2 text-right tabular-nums font-semibold">
                      {currency(amount)}
                    </td>
                    <td className="px-space-2 py-space-2 text-center">
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="p-space-1 rounded-full text-gray-mid hover:text-status-error"
                        aria-label="Remove row"
                      >
                        <Trash2 size={16} strokeWidth={1.5} aria-hidden />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {rows.some((r) => r.error) && (
                <tr>
                  <td colSpan={8} className="px-space-3 py-space-2 bg-status-error/5 text-small text-status-error">
                    Fix the rows flagged in red, or remove them — they'll be skipped on submit.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-border-light bg-white-warm">
                <td colSpan={6} className="px-space-3 py-space-2 text-right font-display text-label font-semibold uppercase tracking-label text-gray-mid">
                  {totalValid} of {rows.length} valid · Total commission
                </td>
                <td className="px-space-3 py-space-2 text-right tabular-nums font-bold">
                  {currency(totalCommission)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      </Card>

      <div className="flex justify-end gap-space-2">
        <Button variant="secondary" onClick={() => navigate("/commissions")}>
          Cancel
        </Button>
        <Button
          variant="primary"
          loading={submitAll.isPending}
          disabled={totalValid === 0 || submitAll.isPending}
          onClick={() => {
            setTopError(null);
            submitAll.mutate();
          }}
        >
          Submit {totalValid} {totalValid === 1 ? "claim" : "claims"}
        </Button>
      </div>
    </div>
  );
}
